import { buildInstrumentKeyCandidates } from './quoteCacheCandidates';
import { MarketQuote } from './marketDataService';

type QuoteResponse = {
    instrumentKey?: string;
    providerId?: string;
    canonicalQuoteKey?: string;
    price?: number;
    rawPrice?: number;
    currency?: string;
    rawCurrency?: string | null;
    quoteUnit?: string | null;
    unitScale?: 1 | 0.01;
    source?: string;
    asOf?: string;
    fetchedAt?: string;
};

const getQuoteEndpointBase = () => {
    const viteBase = import.meta.env?.VITE_QUOTE_ENDPOINT_BASE_URL;
    const nodeBase = typeof process !== 'undefined' ? process.env?.VITE_QUOTE_ENDPOINT_BASE_URL : undefined;
    return (viteBase || nodeBase || '').replace(/\/$/, '');
};

const buildEndpoint = (instrumentKey: string, force?: boolean) => {
    const base = getQuoteEndpointBase();
    if (!base) return null;
    const query = new URLSearchParams({ instrumentKey });
    if (force) query.set('force', '1');
    return `${base}/getQuote?${query.toString()}`;
};

const RETRYABLE_STATUSES = new Set([429, 502, 503, 504]);
const DEFAULT_RETRY_DELAY_MS = 400;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getRetryDelayMs = (response: Response) => {
    const retryAfter = response.headers.get('retry-after');
    if (!retryAfter) return DEFAULT_RETRY_DELAY_MS;
    const seconds = Number.parseFloat(retryAfter);
    if (Number.isFinite(seconds)) {
        return Math.max(seconds * 1000, DEFAULT_RETRY_DELAY_MS);
    }
    const retryDate = Date.parse(retryAfter);
    if (!Number.isNaN(retryDate)) {
        return Math.max(retryDate - Date.now(), DEFAULT_RETRY_DELAY_MS);
    }
    return DEFAULT_RETRY_DELAY_MS;
};

const fetchWithRetry = async (url: string): Promise<Response> => {
    let response = await fetch(url);
    if (!response.ok && RETRYABLE_STATUSES.has(response.status)) {
        await delay(getRetryDelayMs(response));
        response = await fetch(url);
    }
    return response;
};

const isQuoteDebugEnabled = () => {
    try {
        return localStorage.getItem('debugQuotes') === '1';
    } catch {
        return false;
    }
};

const logCachedQuote = (quote: MarketQuote, debugRawPrice: number | undefined, quoteUnit: string | null | undefined, source: string | undefined) => {
    if (!isQuoteDebugEnabled()) return;
    console.debug('[quotes] cached quote', {
        providerId: quote.providerId,
        rawPrice: debugRawPrice ?? quote.rawPrice,
        price: quote.price,
        rawCurrency: quote.rawCurrency,
        currency: quote.currency,
        quoteUnit,
        unitScale: quote.unitScale,
        source,
        isBackendNormalizedQuote: true,
    });
};

export const fetchCachedQuote = async (
    instrumentKey: string,
    opts?: { force?: boolean }
): Promise<MarketQuote | null> => {
    if (!instrumentKey) return null;
    try {
        const candidates = buildInstrumentKeyCandidates(instrumentKey);
        if (!candidates.length) return null;

        for (const candidate of candidates) {
            const url = buildEndpoint(candidate, opts?.force);
            if (!url) {
                console.warn('Quote cache endpoint is not configured. Skipping quote fetch.');
                return null;
            }

            let response = await fetchWithRetry(url);
            if (!response.ok) {
                const shouldWarn = response.status !== 404;
                if (shouldWarn) {
                    console.warn('Quote cache request failed', response.status, response.statusText, { instrumentKey: candidate });
                }
                if (opts?.force) {
                    const retryUrl = buildEndpoint(candidate, false);
                    if (!retryUrl) return null;
                    response = await fetchWithRetry(retryUrl);
                    if (!response.ok) {
                        if (response.status !== 404) {
                            console.warn('Quote cache retry failed', response.status, response.statusText, { instrumentKey: candidate });
                        }
                        continue;
                    }
                } else {
                    continue;
                }
            }

            const json = (await response.json()) as QuoteResponse;
            const price = Number(json?.price);
            if (!Number.isFinite(price) || !json?.currency) {
                continue;
            }

            const providerId = json.providerId || json.instrumentKey || candidate;
            const unitScale = Number(json.unitScale);
            const debugRawPrice = Number(json.rawPrice);
            const quote = {
                providerId,
                // Cached backend quote.price is already normalized and display-ready.
                // Keep raw provider price out of MarketQuote.rawPrice so legacy detail
                // code cannot accidentally normalize the stored price a second time.
                rawPrice: price,
                rawCurrency: json.rawCurrency || json.currency || null,
                price,
                currency: json.currency,
                asOf: json.asOf || new Date().toISOString(),
                fetchedAt: json.fetchedAt,
                unitScale: unitScale === 0.01 ? 0.01 : 1,
                quoteUnit: json.quoteUnit,
                source: json.source,
                canonicalQuoteKey: json.canonicalQuoteKey,
                providerRawPrice: Number.isFinite(debugRawPrice) ? debugRawPrice : undefined,
            } as MarketQuote & { quoteUnit?: string | null; source?: string; providerRawPrice?: number };
            logCachedQuote(quote, Number.isFinite(debugRawPrice) ? debugRawPrice : undefined, json.quoteUnit, json.source);
            return quote;
        }

        return null;
    } catch (error) {
        console.error('Failed to fetch cached quote', error);
        return null;
    }
};

export const fetchCachedQuotesQueued = async (
    instrumentKeys: string[],
    onQuote: (quoteKey: string, q: MarketQuote) => void,
    opts?: { maxTotal?: number; force?: boolean }
): Promise<{ updated: number; attempted: number }> => {
    const uniq = Array.from(new Set(instrumentKeys.filter(Boolean))).slice(0, opts?.maxTotal ?? 12);
    let updated = 0;

    for (const key of uniq) {
        const q = await fetchCachedQuote(key, { force: opts?.force });
        if (q) {
            onQuote(key, q);
            updated += 1;
        }
    }

    return { updated, attempted: uniq.length };
};
